import { useEffect, useMemo } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { getTalentMobilityThresholds } from 'Api/apiService';

/**
 * useTalentMobilitySettingsStore
 *
 * Admin-configurable Talent Mobility thresholds (req #8: green/amber/red
 * match banding, plus the org-view "Ready for Next Role" threshold).
 * Session-only by design — Talent Mobility data must not live in
 * localStorage; the thresholds API is the persistent source and consumers
 * (Utils/scoreColor.utils.js etc.) read from here after it hydrates.
 */
const useTalentMobilitySettingsStore = create(
  devtools(
    (set) => ({
      matchGreenThreshold: 80,
      matchRedThreshold: 50,
      successionReadyThreshold: 90,
      // "Ready in 1 Year" — SRI at/above this (but below successionReadyThreshold)
      // marks a successor as ready within a year, one of the 3 bands
      // (Ready Now / Ready in 1yr / No immediate successor) that feed the
      // Succession Score formula in Utils/successionScore.utils.js (Engine D).
      successionOneYearThreshold: 75,
      // Whether shortlisting a candidate from the Candidate Profile view
      // notifies the employee — see useShortlistStore.js. Off = silent
      // shortlisting, no simulated notification.
      matchNotificationsEnabled: true,
      // Admin "Open Role Visibility" toggle (thresholds API
      // is_open_role_visible): when off, open roles are hidden across
      // Talent Mobility — e.g. no "Open" tag on employee pathway cards.
      isOpenRoleVisible: true,

      setThresholds: (patch) => set(patch),
      setMatchNotificationsEnabled: (matchNotificationsEnabled) => set({ matchNotificationsEnabled }),
      setOpenRoleVisible: (isOpenRoleVisible) => set({ isOpenRoleVisible }),
    }),
    { name: 'TalentMobilitySettingsStore' }
  )
);

// Fetches GET /talent-mobility/thresholds and applies it to the store,
// unconditionally. Returns the promise so callers can await/parallelize it
// (TalentMobilityEmployeeV2 refreshes thresholds alongside POST /career-paths
// on Save & Continue); a failure resets the hydration flag so a later
// threshold-reading mount retries instead of pinning the defaults.
let thresholdsHydrationStarted = false;
export const refreshThresholdsFromApi = () => {
  thresholdsHydrationStarted = true;
  return getTalentMobilityThresholds()
    .then((fetched) => {
      const { setThresholds, setMatchNotificationsEnabled, setOpenRoleVisible } =
        useTalentMobilitySettingsStore.getState();
      setThresholds(toThresholdsPatch(fetched));
      setMatchNotificationsEnabled(Boolean(fetched.matchNotificationsEnabled));
      // snake_case end-to-end by design (see TalentMobilitySettings.jsx);
      // missing/null means the org never saved the toggle — default visible.
      setOpenRoleVisible(fetched.is_open_role_visible !== false);
    })
    .catch((error) => {
      thresholdsHydrationStarted = false;
      throw error;
    });
};

// Hydrates the store once per session — with localStorage persistence gone,
// this is what carries the org's saved thresholds to screens other than the
// admin settings page (which does its own fetch). First threshold-reading
// hook to mount wins.
const hydrateThresholdsFromApi = () => {
  if (thresholdsHydrationStarted) return;
  refreshThresholdsFromApi().catch(() => {
    // flag already reset by refreshThresholdsFromApi; next mount retries
  });
};

// Selects the two primitives separately (stable references) and memoizes the
// composed object — a selector returning `{...}` fresh on every call is never
// referentially stable, which with Zustand v4's useSyncExternalStore forces a
// render loop ("Maximum update depth exceeded") rather than just extra
// renders. Every Talent Mobility screen calls this, so this bug was systemic.
export const useMatchThresholds = () => {
  useEffect(hydrateThresholdsFromApi, []);
  const green = useTalentMobilitySettingsStore((state) => state.matchGreenThreshold);
  const red = useTalentMobilitySettingsStore((state) => state.matchRedThreshold);
  return useMemo(() => ({ green, red }), [green, red]);
};

// Admin "Open Role Visibility" toggle — single primitive, so no memo needed.
export const useIsOpenRoleVisible = () => {
  useEffect(hydrateThresholdsFromApi, []);
  return useTalentMobilitySettingsStore((state) => state.isOpenRoleVisible);
};

// Same stable-selector pattern as useMatchThresholds — feeds the Succession
// Score formula (Utils/successionScore.utils.js, Engine D) and the org-chart's
// "Ready for Next Role" / "Ready in 1 Year" banding.
export const useSuccessionThresholds = () => {
  useEffect(hydrateThresholdsFromApi, []);
  const readyNow = useTalentMobilitySettingsStore((state) => state.successionReadyThreshold);
  const readyOneYear = useTalentMobilitySettingsStore((state) => state.successionOneYearThreshold);
  return useMemo(() => ({ readyNow, readyOneYear }), [readyNow, readyOneYear]);
};

// Non-reactive snapshot of just the persisted fields (no action functions) —
// for one-off reads outside a component's render cycle, e.g. seeding a form's
// initial state (TalentMobilitySettings.jsx) without subscribing to the store.
export const getStoredThresholdsSnapshot = () => {
  const {
    matchGreenThreshold,
    matchRedThreshold,
    successionReadyThreshold,
    successionOneYearThreshold,
    matchNotificationsEnabled,
  } = useTalentMobilitySettingsStore.getState();
  return {
    matchGreenThreshold,
    matchRedThreshold,
    successionReadyThreshold,
    successionOneYearThreshold,
    matchNotificationsEnabled,
  };
};

// Projects a { ...threshold fields, matchNotificationsEnabled } object (the
// shape TalentMobilitySettings.jsx's form and the thresholds API both use)
// down to just the four fields setThresholds expects — notifications has its
// own action (setMatchNotificationsEnabled) and is intentionally excluded.
export const toThresholdsPatch = ({
  matchGreenThreshold,
  matchRedThreshold,
  successionReadyThreshold,
  successionOneYearThreshold,
}) => ({
  matchGreenThreshold,
  matchRedThreshold,
  successionReadyThreshold,
  successionOneYearThreshold,
});

export default useTalentMobilitySettingsStore;
